import { Button, Form, IFormRender, Label, TextareaFormField, TextFormField, Theme } from '@busybusy/webapp-react-ui';
import { GiftOutlineIcon } from 'assets/icons';
import PhoneFormField from 'components/foundation/forms/PhoneFormField/PhoneFormField';
import useSlackReferral, {
  IReferralSubmission,
} from 'containers/settings/integrations/vendors/slack/hooks/useSlackReferral';
import { useLoader } from 'contexts/LoaderContext';
import { useToastDispatch } from 'contexts/ToastContext';
import useBrandTitle from 'hooks/meta/useBrandTitle';
import { useState } from 'react';
import { captureSentryError } from 'utils/captureSentryError';
import { t } from 'utils/localize';
import { v_require, v_valid_email } from 'utils/validations';
import './ReferralForm.scss';

const EMPTY_REFERRAL: IReferralSubmission = { name: '', company: '', phone: '', email: '', notes: '' };

const EMAIL_VALIDATIONS = [{ validation: v_valid_email }];

// Slack caps a message section at 3000 characters, and every friend field shares one section.
// Phone has no cap here because v_phone already rejects anything that isn't a real number.
const NAME_RESTRICTION = { maxLength: 100 };
const COMPANY_RESTRICTION = { maxLength: 100 };
const EMAIL_RESTRICTION = { maxLength: 254 };
const NOTES_RESTRICTION = { maxLength: 1000 };

export interface IReferralFormProps {
  onSubmitted: () => void;
}

const ReferralForm = ({ onSubmitted }: IReferralFormProps) => {
  const submitReferral = useSlackReferral();
  const toastDispatch = useToastDispatch();
  const loader = useLoader();
  const brand = useBrandTitle();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasFailed, setHasFailed] = useState(false);

  async function handleSubmit(data: IReferralSubmission) {
    setIsSubmitting(true);
    setHasFailed(false);
    loader.open(t('Loading...'));

    let succeeded = false;
    try {
      const response = await submitReferral(data);
      succeeded = response.status === 200;
    } catch (error) {
      captureSentryError(error, 'referral', 'ReferralForm');
    } finally {
      loader.close();
    }

    setIsSubmitting(false);

    if (succeeded) {
      onSubmitted();
    } else {
      setHasFailed(true);
      toastDispatch({
        type: 'OPEN',
        payload: { label: t('Failed to send referral. Try again later.'), theme: Theme.DANGER },
      });
    }
  }

  const renderFields = (form: IFormRender<IReferralSubmission>) => (
    <>
      <Label>{t("Friend's Name")}</Label>
      <TextFormField form={form} name="name" validations={[{ validation: v_require }]} restrictTo={NAME_RESTRICTION} />

      <Label className="mt-4" optional={true}>
        {t('Company')}
      </Label>
      <TextFormField form={form} name="company" restrictTo={COMPANY_RESTRICTION} />

      <PhoneFormField form={form} name="phone" labelClassName="mt-4" />

      <Label className="mt-4" optional={true}>
        {t('Email')}
      </Label>
      <TextFormField
        form={form}
        name="email"
        type="email"
        validations={EMAIL_VALIDATIONS}
        restrictTo={EMAIL_RESTRICTION}
      />

      <Label className="mt-4" optional={true}>
        {t('Anything we should know?')}
      </Label>
      <TextareaFormField form={form} name="notes" restrictTo={NOTES_RESTRICTION} />

      <Button className="mt-6" type="primary" fullWidth={true} disabled={isSubmitting} onClick={form.handleSubmit}>
        {t('Submit Referral')}
      </Button>
      {hasFailed && (
        <div className="referral-form-error" role="alert">
          {t('Failed to send referral. Try again later.')}
        </div>
      )}
    </>
  );

  return (
    <div className="referral-form">
      <section className="referral-form-pitch">
        <div className="referral-form-eyebrow">{t('Refer a Friend')}</div>
        <h1>{t("Know a contractor who'd love {{brand}}?", { brand })}</h1>
        <p>
          {t('If {{brand}} makes your job easier, you probably know someone else who could use it too.', { brand })}
        </p>
        <p>
          {t(
            "Send us their info and we'll take it from there. We'll give them a call, let them know you sent them, and show them how it works. No hard sell."
          )}
        </p>

        <div className="referral-form-reward">
          <h3>
            <GiftOutlineIcon className="referral-form-reward-icon" />
            {t("What's in it for you?")}
          </h3>
          <div className="referral-form-reward-amount">
            <span className="referral-form-reward-currency">$</span>100
          </div>
          <div className="referral-form-reward-unit">{t('Gift Card. Each.')}</div>
          <p>
            {t(
              "Besides helping your buddy stop chasing time cards, if they sign up with {{brand}} we'll send you both a $100 gift card as a thank-you.",
              { brand }
            )}
          </p>
        </div>
      </section>

      <section className="referral-form-card">
        <h4>{t('Tell us about your friend')}</h4>
        <Form<IReferralSubmission>
          data={EMPTY_REFERRAL}
          render={renderFields}
          onSubmit={handleSubmit}
          allowMultipleSubmissions={true}
        />
        <div className="referral-form-footnote">{t('Reward sent once they are an active, paying customer.')}</div>
      </section>
    </div>
  );
};

export default ReferralForm;
